import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { X, ArrowRight } from "lucide-react";
import RoomHeader from "./RoomHeader";
import type { Room, Player, Round, Submission, SubmissionReaction } from "@/hooks/useRoom";
import { toast } from "sonner";

interface Props {
  room: Room;
  players: Player[];
  round: Round | null;
  submissions: Submission[];
  reactions: SubmissionReaction[];
  me: Player;
  amHost: boolean;
  publishReactionSnapshot: (submissionId: string, snapshot: SubmissionReaction[]) => void;
}

const REACTION_EMOJIS = ["👍", "❤️", "🎉", "😂", "🤔", "🔥"] as const;

const reactionKey = (reaction: Pick<SubmissionReaction, "submission_id" | "player_id" | "emoji">) =>
  `${reaction.submission_id}|${reaction.player_id}|${reaction.emoji}`;

type ColumnDef = {
  key: string;
  title: string;
  emoji: string;
  accent: string;
  bar: string;
  border: string;
  tint: string;
};

const DEFAULT_COLUMNS: ColumnDef[] = [
  {
    key: "went-well",
    title: "What went well",
    emoji: "✅",
    accent: "from-emerald-500 to-emerald-600",
    bar: "bg-emerald-500",
    border: "border-l-emerald-500",
    tint: "bg-emerald-500/5",
  },
  {
    key: "didnt-go-well",
    title: "What didn't",
    emoji: "⚠️",
    accent: "from-rose-500 to-rose-600",
    bar: "bg-rose-500",
    border: "border-l-rose-500",
    tint: "bg-rose-500/5",
  },
  {
    key: "ideas",
    title: "Ideas",
    emoji: "💡",
    accent: "from-amber-500 to-amber-600",
    bar: "bg-amber-500",
    border: "border-l-amber-500",
    tint: "bg-amber-500/5",
  },
  {
    key: "actions",
    title: "Action items",
    emoji: "🎯",
    accent: "from-primary to-secondary",
    bar: "bg-primary",
    border: "border-l-primary",
    tint: "bg-primary/5",
  },
];


const MAX_CARD_LENGTH = 280;

const RetroBoard = ({ room, players, round, submissions, reactions, me, amHost, publishReactionSnapshot }: Props) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const toggleReaction = async (submissionId: string, emoji: string) => {
    const current = reactions.filter((r) => r.submission_id === submissionId);
    const mine = current.find((r) => r.player_id === me.id && r.emoji === emoji);

    let next: SubmissionReaction[];
    if (mine) {
      next = current.filter((r) => !(r.player_id === me.id && r.emoji === emoji));
    } else {
      next = [
        ...current,
        {
          id: `optimistic-${submissionId}-${me.id}-${emoji}`,
          submission_id: submissionId,
          player_id: me.id,
          emoji,
        },
      ];
    }

    // Optimistic local update + broadcast to other clients in the room
    publishReactionSnapshot(submissionId, next);

    // Persist to database (idempotent)
    if (mine) {
      const { error } = await supabase
        .from("submission_reactions")
        .delete()
        .eq("submission_id", submissionId)
        .eq("player_id", me.id)
        .eq("emoji", emoji);
      if (error) {
        publishReactionSnapshot(submissionId, current);
        toast.error("Couldn't remove reaction");
      }
    } else {
      const { data, error } = await supabase
        .from("submission_reactions")
        .upsert(
          { submission_id: submissionId, player_id: me.id, emoji },
          { onConflict: "submission_id,player_id,emoji" },
        )
        .select()
        .single();
      if (error) {
        publishReactionSnapshot(submissionId, current);
        toast.error("Couldn't react");
        return;
      }
      if (data) {
        // Replace the optimistic id with the real one
        const finalized = next.map((r) =>
          r.player_id === me.id && r.emoji === emoji && r.submission_id === submissionId
            ? (data as SubmissionReaction)
            : r,
        );
        publishReactionSnapshot(submissionId, finalized);
      }
    }
  };

  const phase = round?.status ?? "collecting";

  const startBoard = async () => {
    setBusy(true);
    const { data: rd, error } = await supabase
      .from("rounds")
      .insert({
        room_id: room.id,
        round_number: 1,
        prompt: "Sprint Retrospective",
        status: "collecting",
      })
      .select()
      .single();
    if (error || !rd) {
      toast.error("Couldn't start board");
      setBusy(false);
      return;
    }
    await supabase.from("rooms").update({ current_round_id: rd.id, status: "playing" }).eq("id", room.id);
    setBusy(false);
  };

  const addCard = async (columnKey: string) => {
    const text = (drafts[columnKey] ?? "").trim();
    if (!text || !round) return;
    setDrafts((d) => ({ ...d, [columnKey]: "" }));
    await supabase.from("submissions").insert({
      round_id: round.id,
      player_id: me.id,
      value: text,
      column_key: columnKey,
    });
  };

  const deleteCard = async (id: string) => {
    await supabase.from("submissions").delete().eq("id", id);
  };



  const goToVoting = async () => {
    if (!round) return;
    await supabase.from("rounds").update({ status: "revealed" }).eq("id", round.id);
    await supabase.from("rooms").update({ status: "revealing" }).eq("id", room.id);
  };

  const finish = async () => {
    if (!round) return;
    await supabase.from("rounds").update({ status: "finished" }).eq("id", round.id);
    await supabase.from("rooms").update({ status: "finished" }).eq("id", room.id);
  };

  if (!round) {
    return (
      <main className="min-h-screen px-4 py-6">
        <div className="max-w-md mx-auto space-y-6">
          <RoomHeader room={room} me={me} amHost={amHost} players={players} />
          <Card className="p-6 border-2 shadow-card text-center space-y-4">
            <div className="text-5xl">🪞</div>
            <h2 className="text-2xl font-extrabold">Sprint Retrospective</h2>
            <p className="text-muted-foreground text-sm">
              Reflect on what went well, what didn't, and what to try next.
              {amHost ? " Tap below when everyone's joined." : " Waiting for the host…"}
            </p>
            {amHost && (
              <Button onClick={startBoard} disabled={busy} className="w-full h-12 gradient-primary font-bold">
                Start board
              </Button>
            )}
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen px-4 py-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <RoomHeader room={room} me={me} amHost={amHost} players={players} />

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="capitalize">
              {phase === "collecting" && "Phase 1 · Add cards"}
              {phase === "revealed" && "Phase 2 · React"}
              {phase === "finished" && "Phase 3 · Discuss"}
            </Badge>
          </div>
          {amHost && phase === "collecting" && (
            <Button onClick={goToVoting} className="font-bold">
              Move to voting <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
          {amHost && phase === "revealed" && (
            <Button onClick={finish} className="font-bold">
              Finish board <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {DEFAULT_COLUMNS.map((col) => {
            const cards = submissions
              .filter((s) => s.column_key === col.key)
              .map((s) => ({
                ...s,
                reactionCount: reactions.filter((r) => r.submission_id === s.id).length,
                authorName: players.find((p) => p.id === s.player_id)?.name ?? "Anon",
              }))
              .sort((a, b) => (phase === "revealed" || phase === "finished" ? b.reactionCount - a.reactionCount : 0));

            const draft = drafts[col.key] ?? "";

            return (
              <div key={col.key} className="rounded-xl bg-muted/30 border border-border/50 overflow-hidden flex flex-col">
                {/* Column header bar */}
                <div className={`${col.bar} px-4 py-3 flex items-center gap-2 text-white`}>
                  <span className="text-lg leading-none">{col.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">{col.title}</div>
                  </div>
                  <Badge variant="secondary" className="bg-white/20 text-white border-0 hover:bg-white/20">
                    {cards.length}
                  </Badge>
                </div>

                <div className="p-3 space-y-3 flex-1">
                  {/* Sticky-note composer */}
                  {phase === "collecting" && (
                    <div className={`rounded-lg ${col.tint} border border-border/60 p-2 space-y-2 shadow-soft`}>
                      <Textarea
                        value={draft}
                        onChange={(e) => setDrafts((d) => ({ ...d, [col.key]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            addCard(col.key);
                          }
                        }}
                        placeholder="Write something…"
                        maxLength={MAX_CARD_LENGTH}
                        rows={2}
                        className="resize-none text-sm bg-background/60 border-0 focus-visible:ring-1 focus-visible:ring-ring min-h-[60px]"
                      />
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-muted-foreground tabular-nums">
                          {draft.length}/{MAX_CARD_LENGTH}
                        </span>
                        <Button
                          size="sm"
                          onClick={() => addCard(col.key)}
                          disabled={!draft.trim()}
                          className="h-8 font-semibold"
                        >
                          Add card
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Cards */}
                  <div className="space-y-2">
                    {cards.map((c) => {
                      const cardReactions = reactions.filter((r) => r.submission_id === c.id);
                      return (
                      <Card
                        key={c.id}
                        className={`p-3 border-l-4 ${col.border} shadow-soft animate-pop-in transition-transform hover:-rotate-1 hover:shadow-card`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <p className="text-sm flex-1 break-words whitespace-pre-wrap leading-snug">{c.value}</p>
                          {c.player_id === me.id && phase === "collecting" && (
                            <button
                              onClick={() => deleteCard(c.id)}
                              className="text-muted-foreground hover:text-destructive flex-shrink-0"
                              aria-label="Delete card"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>

                        {/* Reactions */}
                        <div className="flex flex-wrap items-center gap-1 mt-2">
                          {REACTION_EMOJIS.map((emoji) => {
                            const count = cardReactions.filter((r) => r.emoji === emoji).length;
                            const mine = cardReactions.some((r) => r.emoji === emoji && r.player_id === me.id);
                            const active = count > 0 || mine;
                            return (
                              <button
                                key={emoji}
                                onClick={() => toggleReaction(c.id, emoji)}
                                className={`flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full border transition-bounce ${
                                  mine
                                    ? "bg-primary/15 border-primary/40 text-foreground"
                                    : active
                                      ? "bg-muted/60 border-transparent hover:bg-muted"
                                      : "border-transparent opacity-40 hover:opacity-100 hover:bg-muted"
                                }`}
                                aria-label={`React with ${emoji}`}
                              >
                                <span className="text-sm leading-none">{emoji}</span>
                                {count > 0 && <span className="font-semibold tabular-nums">{count}</span>}
                              </button>
                            );
                          })}
                        </div>

                        <div className="flex items-center mt-3">
                          <span className="text-[10px] text-muted-foreground truncate">{c.authorName}</span>
                        </div>
                      </Card>
                      );
                    })}
                    {cards.length === 0 && phase !== "collecting" && (
                      <div className="text-center text-xs text-muted-foreground py-4">No cards</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
};

export default RetroBoard;
